import BigButton from "components/atoms/buttons/BigButton";
import * as s from "./InformationStyled";
import { ReactComponent as EmptyIcon } from "assets/icons/empty_information.svg";
import { AiOutlinePlus } from "react-icons/ai";
import { ReactComponent as InfoIcon } from "assets/icons/info.svg";
import Input from "components/atoms/input/Input";
import { FaMinusCircle } from "react-icons/fa";
import { ModalInput } from "components/atoms/input/modal/ModalInput";
import PhoneInput from "components/atoms/input/phone/PhoneInput";
import { isPhoneNumberValid, isUrlValid } from "utils/validHelper";
import { ContactInfoItem } from "api/interfaces/helpInterface.interface";
import { checkEmailRegEx } from "utils/regEx";
import { ContactInfo } from "pages/help/contact/ContactPresenter";
import Spinner from "components/atoms/loader/Spinner";
import CenterContentPage from "components/templates/layout/CenterContentPage";
import { toTrim } from "utils/functions";

interface Props {
  step: number;
  setStep: (step: number) => void;
  data: ContactInfoItem;
  hasBeenData: boolean;
  onAddItem: (label: keyof ContactInfoItem) => void;
  onChangeValue: (
    label: keyof ContactInfoItem,
    key: string,
    value: string,
    idx: number
  ) => void;
  onDeleteItem: (label: keyof ContactInfoItem, idx: number) => void;
  onClickReset: () => void;
  onClickSave: () => void;
  validateMsg: Record<string, string>;
  isLoading: boolean;
}

export default function InformationPresenter({
  step,
  setStep,
  hasBeenData,
  data,
  onAddItem,
  onChangeValue,
  onDeleteItem,
  onClickReset,
  onClickSave,
  validateMsg,
  isLoading,
}: Props) {
  const buttons = (
    <>
      <BigButton
        buttonType="cancel"
        onClickBtn={onClickReset}
        label="Back"
        data-testid="setting-information-information-button-click-reset"
      />

      <BigButton
        onClickBtn={onClickSave}
        label="Save"
        data-testid="setting-information-information-button-click-save"
      />
    </>
  );

  return (
    <CenterContentPage
      titleComponent="Information"
      buttonLabel={step === 2 ? "Edit" : undefined}
      onClickButton={
        step === 2
          ? () => {
              setStep(1);
            }
          : undefined
      }
      titleDescription={
        <>
          <InfoIcon />
          This information will be visible to your end-user as well as to your
          partners and organizations
        </>
      }
      buttons={step === 1 ? buttons : undefined}
    >
      {isLoading ? (
        <Spinner isCenter />
      ) : step === 0 ? (
        <s.EmptyContainer>
          <EmptyIcon />
          <p>
            <b>No Information</b> You have not yet added any information
          </p>
          <BigButton
            onClickBtn={() => {
              setStep(1);
            }}
            label="Add Information"
            data-testid="setting-information-information-button-set-step"
          />
        </s.EmptyContainer>
      ) : step === 1 ? (
        <>
          <s.Item>
            <h3>
              <label>Sites</label>
              <s.AddButton
                disabled={(data?.sites || []).length >= 10}
                onClick={() => {
                  onAddItem("sites");
                }}
                data-testid="setting-information-information-add-button-add-item"
              >
                <AiOutlinePlus size={20} />
              </s.AddButton>
            </h3>
            {(data?.sites || []).length > 0 ? (
              (data?.sites || []).map((site, idx) => (
                <s.ValueWrapper key={site.id || idx}>
                  <ModalInput
                    label="Site"
                    hiddenLabel={idx > 0}
                    essential={true}
                    isValidated={site.site ? !isUrlValid(site.site) : false}
                    validatedWord={validateMsg.sites}
                  >
                    <Input
                      value={site.site || ""}
                      onChange={(e) => {
                        onChangeValue("sites", "site", e.target.value, idx);
                      }}
                      data-testid="setting-information-information-input-change-value"
                    />
                  </ModalInput>
                  <s.DeleteButton
                    index={idx}
                    onClick={() => {
                      onDeleteItem("sites", idx);
                    }}
                    data-testid="setting-information-information-delete-button-delete-item"
                  >
                    <FaMinusCircle />
                  </s.DeleteButton>
                </s.ValueWrapper>
              ))
            ) : (
              <p className="empty-message">You haven't added any sites yet</p>
            )}
          </s.Item>
          <s.Item>
            <h3>
              <label>Phone numbers</label>
              <s.AddButton
                disabled={(data?.phones || []).length >= 10}
                onClick={() => {
                  onAddItem("phones");
                }}
                data-testid="setting-information-information-add-button-add-item-2"
              >
                <AiOutlinePlus size={20} />
              </s.AddButton>
            </h3>
            {(data?.phones || []).length > 0 ? (
              (data?.phones || []).map((phone, idx) => (
                <s.ValueWrapper key={phone.id || idx}>
                  <ModalInput
                    label="Phone"
                    hiddenLabel={idx > 0}
                    essential={true}
                    isValidated={
                      phone.phone ? !isPhoneNumberValid(phone.phone) : false
                    }
                    validatedWord={validateMsg.phone}
                  >
                    <PhoneInput
                      selected={phone.phone || ""}
                      placeholder="Please enter a phone number"
                      onChange={(phoneNumber: string) =>
                        onChangeValue("phones", "phone", phoneNumber, idx)
                      }
                      data-testid="setting-information-information-phone-input-change-value"
                    />
                  </ModalInput>
                  <ModalInput label="Description" hiddenLabel={idx > 0}>
                    <Input
                      value={phone.description}
                      onChange={(e) => {
                        onChangeValue(
                          "phones",
                          "description",
                          e.target.value,
                          idx
                        );
                      }}
                      data-testid="setting-information-information-input-change-value-2"
                    />
                  </ModalInput>
                  <s.DeleteButton
                    index={idx}
                    onClick={() => {
                      onDeleteItem("phones", idx);
                    }}
                    data-testid="setting-information-information-delete-button-delete-item-2"
                  >
                    <FaMinusCircle />
                  </s.DeleteButton>
                </s.ValueWrapper>
              ))
            ) : (
              <p className="empty-message">You haven't added any emails yet</p>
            )}
          </s.Item>
          <s.Item>
            <h3>
              <label>Emails</label>
              <s.AddButton
                disabled={(data?.emails || []).length >= 10}
                onClick={() => {
                  onAddItem("emails");
                }}
                data-testid="setting-information-information-add-button-add-item-3"
              >
                <AiOutlinePlus size={20} />
              </s.AddButton>
            </h3>
            {(data?.emails || []).length > 0 ? (
              (data?.emails || []).map((email, idx) => (
                <s.ValueWrapper key={email.id || idx}>
                  <ModalInput
                    label="Email"
                    hiddenLabel={idx > 0}
                    essential={true}
                    isValidated={
                      email.email ? !checkEmailRegEx(email.email) : false
                    }
                    validatedWord={validateMsg.email}
                  >
                    <Input
                      value={email.email || ""}
                      onChange={(e) => {
                        onChangeValue(
                          "emails",
                          "email",
                          toTrim(e.target.value),
                          idx
                        );
                      }}
                      data-testid="setting-information-information-input-change-value-3"
                    />
                  </ModalInput>
                  <ModalInput label="Description" hiddenLabel={idx > 0}>
                    <Input
                      value={email.description}
                      onChange={(e) => {
                        onChangeValue(
                          "emails",
                          "description",
                          e.target.value,
                          idx
                        );
                      }}
                      data-testid="setting-information-information-input-change-value-4"
                    />
                  </ModalInput>
                  <s.DeleteButton
                    index={idx}
                    onClick={() => {
                      onDeleteItem("emails", idx);
                    }}
                    data-testid="setting-information-information-delete-button-delete-item-3"
                  >
                    <FaMinusCircle />
                  </s.DeleteButton>
                </s.ValueWrapper>
              ))
            ) : (
              <p className="empty-message">
                You haven't added any custom fields yet
              </p>
            )}
          </s.Item>
          <s.Item>
            <h3>
              <label>Custom</label>
              <s.AddButton
                disabled={(data?.customFields || []).length >= 10}
                onClick={() => {
                  onAddItem("customFields");
                }}
                data-testid="setting-information-information-add-button-add-item-4"
              >
                <AiOutlinePlus size={20} />
              </s.AddButton>
            </h3>
            {(data?.customFields || []).length > 0 ? (
              (data?.customFields || []).map((customField, idx) => (
                <s.ValueWrapper key={customField.id || idx}>
                  <ModalInput
                    label="Label"
                    hiddenLabel={idx > 0}
                    essential={true}
                  >
                    <Input
                      value={customField.label || ""}
                      onChange={(e) => {
                        onChangeValue(
                          "customFields",
                          "label",
                          e.target.value,
                          idx
                        );
                      }}
                      data-testid="setting-information-information-input-change-value-5"
                    />
                  </ModalInput>
                  <ModalInput label="Value" hiddenLabel={idx > 0}>
                    <Input
                      value={customField.value || ""}
                      onChange={(e) => {
                        onChangeValue(
                          "customFields",
                          "value",
                          e.target.value,
                          idx
                        );
                      }}
                      data-testid="setting-information-information-input-change-value-6"
                    />
                  </ModalInput>
                  <s.DeleteButton
                    index={idx}
                    onClick={() => {
                      onDeleteItem("customFields", idx);
                    }}
                    data-testid="setting-information-information-delete-button-delete-item-4"
                  >
                    <FaMinusCircle />
                  </s.DeleteButton>
                </s.ValueWrapper>
              ))
            ) : (
              <p className="empty-message">
                You haven't added any custom information yet
              </p>
            )}
          </s.Item>
        </>
      ) : (
        <ContactInfo data={data} />
      )}
    </CenterContentPage>
  );
}
